/* eslint-disable max-lines */

// deps
import React, { ReactElement, memo, useCallback, useContext, useMemo } from "react";

// internal-deps
import {
    CartItemRendererProps,
    FreeCashRemovalPopupVariant,
    GroupCartItemRendererProps,
    GroupOrderAnalyticsPayload,
    ItemImagesOnCartXPVariants,
} from "@swiggy-private/cart-types";
import { CartItemListWidget } from "@swiggy-private/cart-ui";
import { AnalyticsContext, GroupOrderCartContext } from "@swiggy-private/checkout-core";
import { ThunderIcon } from "@cart-client/components/steal-deal-widget-renderer/ThunderIcon";

// helpers
import {
    createCartItemListWithUUID,
    getCartGroupItems,
    getSelectiveDiscountingSections,
    CartItemWithUUID,
    isGroupOrderFlow,
} from "@cart-client/helpers/CartItemRenderer";
import { getMenuCalloutInfo } from "@cart-client/helpers/Cart";
import { isEmpty } from "@swiggy-private/js-utils";

// styles
import styles from "./styles/CartItemRenderer.scss";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { useFreeCashRemovalPopup } from "@cart-client/contexts/FreeCashRemovalPopupContext";

// analytics events
import {
    clickItemIncrement,
    clickItemDecrement,
    clickRemoveSingleOOSItem,
    clickCustomiseUpdateItem,
    impressionNormalCustimsation,
    impressionProgressiveCustimsation,
    impressionCartItemImage,
    impressionItemListHeader,
} from "@cart-client/analytics/events";

// hooks
import { useCartItemrenderer } from "@cart-client/hooks/useCartItemRenderer";
import { useCartItemActions } from "@cart-client/hooks/useCartItemActions";

// constants
import { STEAL_DEAL_CART_ITEM_LIST_HEADER_TITLE } from "@cart-client/constants/index";

type IProps = CartItemRendererProps | GroupCartItemRendererProps;

function CartItemRenderer(props: IProps): ReactElement | null {
    const cartItemsList = useMemo(() => createCartItemListWithUUID(props.cartItems), [props.cartItems]);
    const { state: groupCartState } = useContext(GroupOrderCartContext);
    // get cart context values
    const { updateCartInProgress } = useContext(UpdateCartContext);
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const {
        cartState: { localCart, reviewedCart: cartResponse },
    } = useContext(CartContext);

    const { onItemUpdate, onItemCustomisation, updatingItem, setUpdatingItem } = useCartItemActions({
        isGroupOrderFlow: isGroupOrderFlow(props),
        updateGroupCart: (props as GroupCartItemRendererProps).updateGroupCart,
        cartItemsList,
        restaurantDetails: props.restaurantDetails,
        dishStyle: cartResponse?.data?.menu_items_widget_presentation_data?.dish_style,
    });

    const { onDisabledIncrementItemQtyClick, onImpressionMagicItemsOfferLimit } = useCartItemrenderer();
    const { onPopupImpression, onPopupClick } = useFreeCashRemovalPopup();

    const itemRemovalAnalyticsParams = useMemo(() => ({ warningTrigger: "item_removal" }), []);

    const onImpressionCartUpdateWarning = useCallback(() => {
        onPopupImpression(FreeCashRemovalPopupVariant.ITEM_REMOVAL, itemRemovalAnalyticsParams);
    }, [onPopupImpression, itemRemovalAnalyticsParams]);

    const onClickCartUpdateWarning = useCallback(
        (userAction: string) => {
            onPopupClick(FreeCashRemovalPopupVariant.ITEM_REMOVAL, userAction, itemRemovalAnalyticsParams);
        },
        [onPopupClick, itemRemovalAnalyticsParams],
    );

    // Group Cart Payload
    const groupCartData = useMemo(() => {
        // for functional usage and analytics usage in case Item Renderer is being used on shared-cart
        if (isGroupOrderFlow(props)) {
            return {
                isInitiator: props.isHost,
                isCurrentItemFromSameUser: props.isCurrentItemFromSameUser,
                userId: props.currentUserId,
                isGroupCart: true,
                groupCartId: props.groupCartId,
                restId: String(props.restaurantDetails?.id) || "",
            } as GroupOrderAnalyticsPayload;
        }

        // for analytics usage in case Item Renderer is being used on actual checkout page, but the order is being done through group order flow
        return {
            groupCartId: groupCartState.data?.group_cart_id,
        } as GroupOrderAnalyticsPayload;
    }, [groupCartState.data?.group_cart_id, props]);

    // get all cart group items
    const {
        COUPON_GROUP_ITEMS: couponGroupItems,
        SILD_ITEMS: sildItems,
        FREEBIE_ITEMS: freebieItems,
        STEAL_DEAL_ITEMS: stealDealItems,
        REGULAR_ITEMS: regularItems,
    } = getCartGroupItems(cartItemsList, props.groupTagDetails);

    const selectiveDiscountingCardList = props.cartMenuItemsHeaderList;
    const selectiveDiscountingSections = useMemo(
        () => getSelectiveDiscountingSections(cartItemsList, selectiveDiscountingCardList),
        [cartItemsList, selectiveDiscountingCardList],
    );

    const selectiveDiscountingGroupTagIds = useMemo(() => {
        if (!selectiveDiscountingCardList) return new Set<string>();
        return new Set(selectiveDiscountingCardList.map((card) => card.groupTagId));
    }, [selectiveDiscountingCardList]);

    // Check if any header has empty/null groupTagId (meaning it applies to all items)
    const hasEmptyGroupTagIdHeader = useMemo(() => {
        return selectiveDiscountingCardList?.some((card) => isEmpty(card.groupTagId)) ?? false;
    }, [selectiveDiscountingCardList]);

    const couponGroupItemsFiltered = useMemo(() => {
        // If there's a header with empty groupTagId, all items go to selective discounting sections
        if (hasEmptyGroupTagIdHeader) {
            return [];
        }
        if (!isEmpty(selectiveDiscountingSections)) {
            return couponGroupItems.filter(
                (item) => !selectiveDiscountingGroupTagIds.has(item.item_group_tag_id || ""),
            );
        }
        return couponGroupItems;
    }, [couponGroupItems, selectiveDiscountingGroupTagIds, selectiveDiscountingSections, hasEmptyGroupTagIdHeader]);

    // Regular items should include all items from cartItemsList that:
    // 1. Don't have item_group_tag_id OR
    // 2. Have item_group_tag_id that doesn't match any groupTagId from cart_menu_items_header_list
    const regularItemsFiltered = useMemo(() => {
        // If there's a header with empty groupTagId, all items go to selective discounting sections
        if (hasEmptyGroupTagIdHeader) {
            return [];
        }
        if (!isEmpty(selectiveDiscountingGroupTagIds)) {
            return cartItemsList.filter(
                (item) =>
                    isEmpty(item.item_group_tag_id) || !selectiveDiscountingGroupTagIds.has(item.item_group_tag_id),
            );
        }
        return regularItems;
    }, [cartItemsList, regularItems, selectiveDiscountingGroupTagIds, hasEmptyGroupTagIdHeader]);

    const onClickItemIncrement = useCallback(
        (menuItemId: number, quantity: number, context: string, itemGroupTagId?: string | null) => {
            clickItemIncrement(
                {
                    itemId: String(menuItemId),
                    itemQuantity: quantity,
                    itemType: context,
                    itemGroupTagId,
                },
                clickEvent,
                groupCartData,
            );
        },
        [clickEvent, groupCartData],
    );
    const onClickItemDecrement = useCallback(
        (menuItemId: number, quantity: number, context: string, itemGroupTagId?: string | null) => {
            clickItemDecrement(
                {
                    itemId: String(menuItemId),
                    itemQuantity: quantity,
                    itemType: context,
                    itemGroupTagId,
                },
                clickEvent,
                groupCartData,
            );
        },
        [clickEvent, groupCartData],
    );
    const onClickRemoveSingleOOSItem = useCallback(
        (menuItemId: number, price: number) => {
            clickRemoveSingleOOSItem(
                {
                    itemId: String(menuItemId),
                    price: price,
                },
                clickEvent,
                localCart?.cart_id,
            );
        },
        [clickEvent, localCart?.cart_id],
    );

    const onCustomiseUpdateItem = useCallback(
        (menuItemId: number) => {
            clickCustomiseUpdateItem(String(menuItemId), clickEvent);
        },
        [clickEvent],
    );
    const onImpressionNormalCustomisation = useCallback(
        (menuItemId: number) => {
            impressionNormalCustimsation(String(menuItemId), impressionEvent);
        },
        [impressionEvent],
    );
    const onImpressionProgressCustomisation = useCallback(
        (menuItemId: number) => {
            impressionProgressiveCustimsation(String(menuItemId), impressionEvent);
        },
        [impressionEvent],
    );

    const onItemImageImpression = useCallback(
        (menuItemId: number) => {
            const context = JSON.stringify({
                itemId: menuItemId,
                cartId: localCart?.cart_id,
            });
            impressionCartItemImage(context, impressionEvent);
        },
        [impressionEvent, localCart?.cart_id],
    );

    const onImpressionItemListHeader = useCallback(
        (isDealRush: boolean) => {
            impressionItemListHeader(impressionEvent, isDealRush);
        },
        [impressionEvent],
    );

    const updateItemInProgess = useCallback(
        (item: CartItemWithUUID | null): void => {
            setUpdatingItem(item);
        },
        [setUpdatingItem],
    );

    const commonProps = useMemo(
        () => ({
            analyticsEvent: {
                onClickItemIncrement,
                onClickItemDecrement,
                onClickRemoveSingleOOSItem,
                onCustomiseUpdateItem,
                onImpressionNormalCustomisation,
                onImpressionProgressCustomisation,
                onItemImageImpression,
                onImpressionItemListHeader,
                onImpressionMagicItemsOfferLimit,
                onImpressionCartUpdateWarningD2C: onImpressionCartUpdateWarning,
                onClickCartUpdateWarningD2C: onClickCartUpdateWarning,
            },
            variant: props.itemImagesXP ?? ItemImagesOnCartXPVariants.DEFAULT,
            setUpdatingItem: updateItemInProgess,
            updatingItem,
            onCustomiseItem: onItemCustomisation,
            onUpdateItem: onItemUpdate,
            updateCartInProgress: updateCartInProgress,
            showInputQtyField: props.showInputQtyField,
            isGroupOrderFlow: isGroupOrderFlow(props),
            isHost: groupCartData?.isInitiator,
            isCurrentItemFromSameUser: groupCartData?.isCurrentItemFromSameUser,
            onDisabledIncrementItemQtyClick,
            isIrctcCartSession: props.isIrctcCartSession,
        }),
        [
            onClickItemIncrement,
            onClickItemDecrement,
            onClickRemoveSingleOOSItem,
            onCustomiseUpdateItem,
            onImpressionNormalCustomisation,
            onImpressionProgressCustomisation,
            onItemImageImpression,
            onImpressionItemListHeader,
            onImpressionMagicItemsOfferLimit,
            onImpressionCartUpdateWarning,
            onClickCartUpdateWarning,
            props,
            updateItemInProgess,
            updatingItem,
            onItemCustomisation,
            onItemUpdate,
            updateCartInProgress,
            groupCartData?.isInitiator,
            groupCartData?.isCurrentItemFromSameUser,
            onDisabledIncrementItemQtyClick,
        ],
    );

    const menuCallout = getMenuCalloutInfo(cartResponse);

    // Sanity Check
    if (!props.cartItems || props.cartItems.length < 1) {
        return null;
    }

    // Build sections array with proper separator tracking
    const allSections: Array<{ component: React.ReactNode; showSeparator: boolean }> = [];

    // Add selective discounting sections (no background color)
    selectiveDiscountingSections.forEach((section, index) => {
        allSections.push({
            component: (
                <CartItemListWidget
                    key={`selective-section-${index}`}
                    {...commonProps}
                    itemListHeader={section.header}
                    items={section.items}
                    groupTagDetails={undefined}
                    titleColor={section.titleColor}
                />
            ),
            showSeparator: allSections.length > 0,
        });
    });

    // Add coupon group items
    if (!isEmpty(couponGroupItemsFiltered)) {
        allSections.push({
            component: (
                <CartItemListWidget
                    key="coupon-group-items"
                    {...commonProps}
                    itemListHeader={props.itemListHeader}
                    items={couponGroupItemsFiltered}
                    menuItemsCalloutPresent={menuCallout.isAvailable}
                    groupTagDetails={props.groupTagDetails}
                />
            ),
            showSeparator: allSections.length > 0,
        });
    }

    // Add SILD items
    if (!isEmpty(sildItems)) {
        allSections.push({
            component: (
                <CartItemListWidget
                    key="sild-items"
                    {...commonProps}
                    itemListHeader={props.itemListHeader}
                    items={sildItems}
                    groupTagDetails={props.groupTagDetails}
                />
            ),
            showSeparator: allSections.length > 0,
        });
    }

    // Add steal deal items
    if (!isEmpty(stealDealItems)) {
        allSections.push({
            component: (
                <CartItemListWidget
                    key="steal-deal-items"
                    {...commonProps}
                    itemListHeader={{
                        title: STEAL_DEAL_CART_ITEM_LIST_HEADER_TITLE,
                        subtitle: "",
                        subtitleColor: "",
                        icon: <ThunderIcon />,
                    }}
                    items={stealDealItems}
                    groupTagDetails={props.groupTagDetails}
                />
            ),
            showSeparator: allSections.length > 0,
        });
    }

    // Add freebie items
    if (!isEmpty(freebieItems)) {
        allSections.push({
            component: (
                <CartItemListWidget
                    key="freebie-items"
                    {...commonProps}
                    items={freebieItems}
                    isCouponGroup={!isEmpty(couponGroupItemsFiltered) || !isEmpty(selectiveDiscountingSections)}
                    groupTagDetails={props.groupTagDetails}
                />
            ),
            showSeparator: allSections.length > 0,
        });
    }

    // Add regular items
    if (!isEmpty(regularItemsFiltered)) {
        allSections.push({
            component: (
                <CartItemListWidget
                    key="regular-items"
                    {...commonProps}
                    itemListHeader={props.isValueMeal ? props.itemListHeader : undefined}
                    items={regularItemsFiltered}
                    menuItemsCalloutPresent={menuCallout.isAvailable}
                    groupTagDetails={props.groupTagDetails}
                />
            ),
            showSeparator: allSections.length > 0,
        });
    }

    return (
        <div className={styles.itemsContainer}>
            {allSections.map((section, index) => (
                <React.Fragment key={index}>
                    {section.showSeparator && <div className={styles.sectionSeparator} />}
                    {section.component}
                </React.Fragment>
            ))}
        </div>
    );
}

export default memo(CartItemRenderer);

/**
 * Items Rendering Sequence
 * 1. Coupon Group Items
 * 2. Sild Items
 * 3. Freebie Items
 * 4. Regular Items
 * Out of stock Items are always part of the above groups and not separate
 */
